import React, { useEffect, useState } from "react";
import { FileText, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, canFinancial } from "@/lib/permissions";
import { fmtDate, fmtMoney, todayISO } from "@/lib/format";
import { PageHeader, Loading, Field } from "@/components/common";
import { DataTable, Btn, TextInput, Select } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";

const STATUSES = ["Draft", "Active", "Suspended", "Expired", "Renewal Due", "Cancelled", "Archived"];

export default function Contracts() {
  const { user } = useAuth();
  const fin = canFinancial(user);
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [bus, setBus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ contract_name: "", client_id: "", business_unit_id: "", start_date: todayISO(), end_date: "", renewal_date: "", status: "Active", annual_contract_value: 0, visits_included: 12, price_per_visit: 0, po_number: "", billing_frequency: "Monthly" });

  const load = async () => {
    setLoading(true);
    try { const p = new URLSearchParams(); if (statusFilter) p.set("status", statusFilter); const { data } = await api.get(`/contracts?${p}`); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); api.get("/business-units").then((r) => setBus(r.data)); }, []);
  useEffect(() => { load(); }, [statusFilter]);

  const alerts = rows.filter((r) => r.status === "Renewal Due" || r.status === "Expired" || (r.end_date && r.end_date < todayISO() && r.status === "Active"));

  const save = async () => {
    if (!form.contract_name || !form.client_id) return toast.error("Name and client required");
    try { await api.post("/contracts", form); toast.success("Contract created"); setModal(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "contract_name", label: "Contract", className: "font-medium text-slate-800" },
    { key: "client_name", label: "Client" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "end_date", label: "End / Renewal", render: (r) => <span className="text-xs">{fmtDate(r.end_date)}</span> },
    { key: "visits", label: "Visits", align: "right", render: (r) => <span className="text-xs">{r.visits_completed}/{r.visits_included} <span className="text-slate-400">({r.visits_remaining} left)</span></span> },
    ...(fin ? [{ key: "annual_contract_value", label: "Value", align: "right", render: (r) => <span className="font-medium">{fmtMoney(r.annual_contract_value)}</span> }] : []),
  ];

  return (
    <div>
      <PageHeader title="Contracts & Service Agreements" subtitle="Delivery tracking, renewals and visit allowances" icon={FileText}
        actions={can(user, "create_job_templates") && <Btn onClick={() => setModal(true)} data-testid="new-contract-btn"><Plus size={16} /> New Contract</Btn>} />

      {alerts.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center gap-2 text-amber-700 font-medium text-sm mb-1"><AlertTriangle size={16} /> {alerts.length} contract alert{alerts.length > 1 ? "s" : ""}</div>
          <div className="text-xs text-amber-700/80">{alerts.map((a) => `${a.contract_name} — ${a.status}`).join(" · ")}</div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="max-w-[200px]"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows} rowTestId="contract-row" />}
      </div>

      <Modal open={modal} onOpenChange={setModal} title="New contract" wide
        footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-contract-btn">Save</Btn></>}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Contract name" required><TextInput value={form.contract_name} onChange={(e) => setForm({ ...form, contract_name: e.target.value })} /></Field>
          <Field label="Client" required><Select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}><option value="">Select</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}</Select></Field>
          <Field label="Business unit"><Select value={form.business_unit_id} onChange={(e) => setForm({ ...form, business_unit_id: e.target.value })}><option value="">Select</option>{bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>
          <Field label="Status"><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</Select></Field>
          <Field label="Start date"><TextInput type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
          <Field label="End date"><TextInput type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          <Field label="Visits included"><TextInput type="number" value={form.visits_included} onChange={(e) => setForm({ ...form, visits_included: +e.target.value })} /></Field>
          <Field label="PO number"><TextInput value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} /></Field>
          {fin && <>
            <Field label="Annual value (€)"><TextInput type="number" value={form.annual_contract_value} onChange={(e) => setForm({ ...form, annual_contract_value: +e.target.value })} /></Field>
            <Field label="Price per visit (€)"><TextInput type="number" value={form.price_per_visit} onChange={(e) => setForm({ ...form, price_per_visit: +e.target.value })} /></Field>
          </>}
        </div>
      </Modal>
    </div>
  );
}
